import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { addNotification } from '../../store/slices/uiSlice';
import api from '../../services/api';

const AttendanceModal = ({ attendance, isCreateMode, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    worship_id: '',
    mobile: '',
    event_id: '',
    section_id: '',
    current_datetime: '',
    member_name: ''
  });
  const [events, setEvents] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    fetchEvents();
    if (attendance && !isCreateMode) {
      setFormData({
        worship_id: attendance.worship_id || '',
        mobile: attendance.mobile || '',
        event_id: attendance.event_id || '',
        section_id: attendance.section_id || '',
        current_datetime: attendance.current_datetime || '',
        member_name: attendance.member_name || ''
      });
    }
  }, [attendance, isCreateMode]);

  useEffect(() => {
    if (formData.event_id) {
      fetchEventSections(formData.event_id);
    } else {
      setSections([]);
    }
  }, [formData.event_id]);

  const fetchEvents = async () => {
    try {
      const response = await api.get('/api/v1/events/datatable?start=0&length=1000');
      setEvents(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const fetchEventSections = async (eventId) => {
    try {
      const response = await api.get(`/api/v1/events/${eventId}`);
      setSections(response.data.sections || []);
    } catch (error) {
      console.error('Failed to fetch sections:', error);
    }
  };

  const formatCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}${month}${day}${hours}${minutes}`;
  };

  const validateForm = () => {
    const errors = {};
    
    // At least one identifier must be provided (worship_id or mobile)
    const hasWorshipId = formData.worship_id.trim();
    const hasMobile = formData.mobile.trim();
    
    if (!hasWorshipId && !hasMobile) {
      errors.worship_id = 'Either Worship ID or Mobile number is required';
    } else if (hasWorshipId && !/^[A-Za-z0-9]+$/.test(hasWorshipId)) {
      errors.worship_id = 'Worship ID must be alphanumeric';
    }
    
    if (!formData.event_id) {
      errors.event_id = 'Event is required';
    }
    
    if (!formData.section_id) {
      errors.section_id = 'Section is required';
    }
    
    if (!formData.current_datetime.trim()) {
      errors.current_datetime = 'QR DateTime is required';
    } else if (!/^\d{12}$/.test(formData.current_datetime.trim())) {
      errors.current_datetime = 'QR DateTime must be 12 digits (YYYYMMDDHHMM)';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      const payload = {
        worship_id: formData.worship_id.trim() ? formData.worship_id.trim() : null,
        mobile: formData.mobile.trim() || null,
        event_id: parseInt(formData.event_id),
        section_id: parseInt(formData.section_id),
        current_datetime: formData.current_datetime.trim()
      };

      if (isCreateMode) {
        await api.post('/api/v1/attendance/', payload);
        dispatch(addNotification({
          type: 'success',
          title: 'Success!',
          message: 'Attendance record created successfully'
        }));
      } else {
        await api.put(`/api/v1/attendance/${attendance.id}`, payload);
        dispatch(addNotification({
          type: 'success',
          title: 'Success!',
          message: 'Attendance record updated successfully'
        }));
      }
      
      onSuccess();
      onClose();
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        title: 'Error!',
        message: error.response?.data?.detail || `Failed to ${isCreateMode ? 'create' : 'update'} attendance record`
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Reset section when event changes
    if (name === 'event_id') {
      setFormData(prev => ({ ...prev, section_id: '' }));
    }
  };

  const handleFillCurrentDateTime = () => {
    const currentDateTime = formatCurrentDateTime();
    setFormData(prev => ({ ...prev, current_datetime: currentDateTime }));
    if (validationErrors.current_datetime) {
      setValidationErrors(prev => ({ ...prev, current_datetime: '' }));
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900">
              {isCreateMode ? 'Create Attendance Record' : 'Edit Attendance Record'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={loading}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Worship ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Worship ID *
              </label>
              <input
                type="text"
                name="worship_id"
                value={formData.worship_id}
                onChange={handleInputChange}
                placeholder="Enter worship ID (numbers only)"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  validationErrors.worship_id ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={loading}
              />
              {validationErrors.worship_id && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.worship_id}</p>
              )}
            </div>

            {/* Mobile Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mobile Phone
              </label>
              <input
                type="text"
                name="mobile"
                value={formData.mobile}
                onChange={handleInputChange}
                placeholder="Enter mobile phone number (e.g., 98765432)"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  validationErrors.mobile ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={loading}
              />
              {validationErrors.mobile && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.mobile}</p>
              )}
            </div>

            {/* Member Name (Read-only) */}
            {!isCreateMode && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Member Name
                </label>
                <input
                  type="text"
                  value={formData.member_name}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                />
              </div>
            )}

            {/* Event Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Event *
              </label>
              <select
                name="event_id"
                value={formData.event_id}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  validationErrors.event_id ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={loading}
              >
                <option value="">Select an event</option>
                {events.map(event => (
                  <option key={event.id} value={event.id}>
                    {event.event_name}
                    {event.venue && ` (${event.venue})`}
                  </option>
                ))}
              </select>
              {validationErrors.event_id && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.event_id}</p>
              )}
            </div>

            {/* Section Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Section *
              </label>
              <select
                name="section_id"
                value={formData.section_id}
                onChange={handleInputChange}
                disabled={!formData.event_id || loading}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 ${
                  validationErrors.section_id ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select a section</option>
                {sections.map(section => (
                  <option key={section.id} value={section.id}>
                    {section.section_name || `Section ${section.id}`}
                    {section.lecturer && ` - ${section.lecturer}`}
                  </option>
                ))}
              </select>
              {!formData.event_id && (
                <p className="mt-1 text-sm text-gray-500">Select an event first</p>
              )}
              {validationErrors.section_id && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.section_id}</p>
              )}
            </div>

            {/* QR DateTime */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                QR DateTime *
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  name="current_datetime"
                  value={formData.current_datetime}
                  onChange={handleInputChange}
                  placeholder="YYYYMMDDHHMM (e.g., 202508171430)"
                  className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    validationErrors.current_datetime ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={handleFillCurrentDateTime}
                  className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-md text-sm transition-colors"
                  disabled={loading}
                >
                  Use Current
                </button>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Format: YYYYMMDDHHMM (Year, Month, Day, Hour, Minute)
              </p>
              {validationErrors.current_datetime && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.current_datetime}</p>
              )}
            </div>

            {/* Buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-md transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                disabled={loading}
              >
                {loading && (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {isCreateMode ? 'Create Record' : 'Update Record'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AttendanceModal;
